<script>
  import { mapGetters } from 'vuex';
  import breakpoints from '@/mixins/breakpoints';
  import sidebarIcons from '@/layouts/vertical/sidebarIcons';
  import anime from 'animejs/lib/anime.es.js';
  import BonusAndBalance from '@/layouts/vertical/BonusAndBalance.vue';
  import { navigateTo } from '@/helpers/router-navigation';

  export default {
    name: 'VerticalSideBarComponent',

    components: {
      BonusAndBalance,
      ...sidebarIcons,
    },

    mixins: [breakpoints],

    data: () => ({
      drawer: false,
      chatIsHidden: true,
    }),

    computed: {
      ...mapGetters(['getSideBarState', 'isUserBanned', 'getPromoPackages', 'getPurchasedPromoPacks']),

      isUserSuspended() {
        return this.$store.getters.isUserSuspended;
      },

      isActiveRoute() {
        return (route) => {
          const currentPath = this.$route.path;

          if (route === '/profileuser' && currentPath.startsWith('/profileuser')) {
            return true;
          }

          return this.$route.matched.some((r) => r.path === route);
        };
      },

      promoCount() {
        const purchased = this.getPurchasedPromoPacks;
        const packages = this.getPromoPackages.filter((p) => !purchased.includes(`${p.mmcAmount}-${p.mqcAmount}`));
        return packages.length;
      },

      /**
       * Returns an array of sidebar menu items.
       * @returns {Array<Object>} The sidebar menu items.
       * @property {string} title - The title of the menu item.
       * @property {Object} icon - The icon component for the menu item.
       * @property {string} to - The route path the menu item navigates to.
       * @property {boolean} [dividerOnTop] - Whether to show a divider above the menu item.
       * @property {boolean} [smallerIcon] - Whether the icon should be smaller (20px in height).
       * @property {boolean} [visible] - Whether the menu item is visible.
       * @property {string} [badge] - The badge text to display right next to the menu item.
       * @property {boolean} [squareBadge] - Whether the badge should be square (4px border radius).
       * @property {boolean} [suspended] - Whether the menu item is visible for suspended users.
       * @property {string} [color] - The color of the menu item.
       * @property {Function} [callback] - The callback function to execute on click.
       */
      items() {
        return [
          {
            title: 'My Account',
            icon: sidebarIcons.IconAccount,
            to: '/profileuser',
            suspended: true,
            visible: this.userSignedIn,
          },

          {
            title: 'Lobby',
            icon: sidebarIcons.IconLobby,
            to: '/lobby',
            dividerOnTop: true,
          },
          // {
          //   title: 'Promotions',
          //   icon: sidebarIcons.IconPromo,
          //   to: '/playerrewards',
          //   badge: this.promoCount,
          //   visible: this.userSignedIn,
          // },
          {
            title: 'Player vs Player',
            icon: sidebarIcons.IconPvp,
            to: '/playervsplayer',
            visible: this.userSignedIn,
          },
          // {
          //   title: 'FastPlay',
          //   icon: sidebarIcons.IconFastplay,
          //   to: '/fastplay',
          //   visible: this.userSignedIn,
          // },
          {
            title: 'Fish Table Games',
            icon: sidebarIcons.IconFishTableGames,
            to: '/fish-table-games',
            visible: this.userSignedIn,
            useStrokeNotFill: true,
          },
          {
            title: 'Challenge Friends',
            icon: sidebarIcons.IconCAF,
            to: '/challenge-friends',
            visible: this.userSignedIn,
          },
          {
            title: 'Refer Friends',
            icon: sidebarIcons.IconRAF,
            to: '/referfriends',
            visible: this.userSignedIn,
          },

          {
            dividerOnTop: true,
            title: 'Mines',
            icon: sidebarIcons.IconMines,
            to: '/lobby/multi-mines',
            visible: this.userSignedIn,
            badge: 'NEW',
            squareBadge: true,
          },
          {
            title: 'Plinko',
            icon: sidebarIcons.IconPlinko,
            to: '/lobby/plinko',
            visible: this.userSignedIn,
          },
          {
            title: 'Max Blast',
            icon: sidebarIcons.IconMaxBlast,
            to: '/lobby/maxblast',
            visible: this.userSignedIn,
          },
          {
            title: 'Sector X',
            icon: sidebarIcons.IconSectorX,
            to: '/lobby/sector-x',
            visible: this.userSignedIn,
          },
          {
            title: 'Mission: Amazon',
            icon: sidebarIcons.IconMissionAmazon,
            to: '/lobby/mission-amazon',
            visible: this.userSignedIn,
          },
          {
            title: 'Dragon Stone',
            icon: sidebarIcons.IconDragonStone,
            to: '/lobby/dragonstone',
            visible: this.userSignedIn,
          },

          {
            title: 'Live Chat',
            icon: sidebarIcons.IconLiveChat,
            visible: this.userSignedIn,
            suspended: true,
            dividerOnTop: true,
            callback: () => this.toggleChat(),
          },

          {
            title: 'Logout',
            icon: sidebarIcons.IconLogout,
            dividerOnTop: true,
            visible: this.userSignedIn,
            suspended: true,
            callback: () => this.logoutUser(),
          },
        ];
      },

      menuItems() {
        const isUserSuspended = this.isUserSuspended;

        if (this.isUserBanned) {
          return this.items.filter((entry) => ['/', '/contact'].includes(entry.to));
        }

        return this.items.filter(
          (entry) => entry.visible !== false && (!isUserSuspended || (isUserSuspended && entry.suspended)),
        );
      },

      userSignedIn() {
        return !!this.$store.getters.getuser;
      },

      totalNotificationAmount() {
        return this.menuItems.reduce((acc, val) => acc + (val.badge ? (isNaN(val.badge) ? 1 : val.badge) : 0), 0);
      },

      iframeDialog() {
        return this.$store.state.dialog.iframeDialog;
      },
    },

    watch: {
      $route: {
        deep: true,
        async handler() {
          if (this.$vuetify.breakpoint.lgAndUp) return;
          await this.$nextTick();
          this.drawer = !this.isMobileOrTablet;
        },
      },

      drawer() {
        const opened = this.isMobileOrTablet ? this.drawer : !this.drawer;

        this.$store.dispatch('toggleSidebar', this.drawer);

        this.animateMenuItems(opened ? 'open' : 'close', 0);
      },

      getSideBarState() {
        this.drawer = this.getSideBarState;
      },

      iframeDialog(value) {
        if (typeof value === 'object' && typeof window.$zoho !== 'undefined' && $zoho.salesiq) {
          this.hideChat();
        }
      },

      totalNotificationAmount(value) {
        this.$store.commit('SET_TOTAL_NOTIFICATION_AMOUNT', value);
      },
    },

    created() {
      this.chatIsHidden = window.zohoChatIsHidden === true;
      if (!this.chatIsHidden) this.toggleChat();
    },

    async mounted() {
      if (this.$vuetify.breakpoint.width > 960) {
        await this.$nextTick();
        this.drawer = false;
      }
    },

    methods: {
      handleMenuItemClick(item) {
        if (item.callback) {
          item.callback();
        } else {
          navigateTo(item.to);
        }
      },

      toggleChat() {
        if (typeof window.$zoho === 'undefined' || !$zoho.salesiq) return;

        if (this.chatIsHidden) this.showChat();
        else this.hideChat();
      },

      showChat() {
        this.chatIsHidden = false;
        $zoho.salesiq.floatbutton?.visible('show');
        $zoho.salesiq.floatwindow?.open();
      },

      hideChat() {
        this.chatIsHidden = true;
        $zoho.salesiq.floatbutton?.visible('hide');
        $zoho.salesiq.floatwindow?.close();
      },

      logoutUser() {
        this.$store.dispatch('signUserOut');

        if (this.$route.path !== '/') {
          this.$router.push('/');
        }
      },

      toggleSidebar(value) {
        this.drawer = value;
      },

      animateMenuItems(direction, delay) {
        anime({
          targets: '.menu-item',
          translateX: direction === 'open' ? [-50, 0] : [50, 0],
          opacity: [0, 1],
          easing: 'easeOutQuad',
          duration: 200,
          delay: anime.stagger(delay, { start: 100 }),
        });
      },
      closeBalanceSelect() {
        this.$refs?.balanceSelect?.closeBalanceSelect();
      },
    },
  };
</script>

<template>
  <v-navigation-drawer
    v-model="drawer"
    :class="!isMobileOrTablet && drawer ? 'drawer-close' : ''"
    :mini-variant="!isMobileOrTablet ? drawer : false"
    :mini-variant-width="120"
    :mobile-breakpoint="990"
    :permanent="!isMobileOrTablet"
    app
    class="border-none outline-none nav-drawer px-6"
    dark
    height="100%"
    touchless
    width="389"
  >
    <v-btn v-if="$vuetify.breakpoint.smAndDown" class="close-menu-btn" icon @click="drawer = !drawer">
      <v-icon>mdi-chevron-left</v-icon>
    </v-btn>
    <v-list class="py-0" dark>
      <v-list-item v-if="!isMobileOrTablet" class="d-flex justify-start align-center menu-toggle-container">
        <v-btn
          :height="52"
          :ripple="false"
          :width="52"
          class="menu-toggle"
          id="menu-toggle"
          color="white"
          icon
          @click="drawer = !drawer"
        >
          <v-app-bar-nav-icon :ripple="false" />
          <span v-if="totalNotificationAmount" class="badge">
            {{ totalNotificationAmount }}
          </span>
        </v-btn>
      </v-list-item>

      <div v-show="$vuetify.breakpoint.smAndDown" class="logo-wrapper" @click="$router.push('/lobby')">
        <img alt="MaxQuest" src="/images/logos/maxquest.svg" />
      </div>

      <div
        :class="{ 'scrollable--suspended': isUserSuspended, mini: drawer }"
        class="scrollable"
        @scroll="closeBalanceSelect"
      >
        <BonusAndBalance ref="balanceSelect" v-if="userSignedIn" :mini="isMobileOrTablet ? !drawer : drawer" />

        <div v-for="(item, index) in menuItems" :key="`${index}_${item.to}`">
          <hr v-if="item.dividerOnTop" class="sidebar-divider" />

          <v-tooltip :content-class="drawer ? 'hidden-tooltip-mobile' : 'hidden-tooltip'" right>
            <template v-slot:activator="{ on }">
              <v-list-item
                :key="index"
                :class="{ 'active-route': isActiveRoute(item.to), colored: !!item.color }"
                :style="{ color: item.color }"
                class="menu-item"
                @click="handleMenuItemClick(item)"
                v-on="on"
              >
                <v-list-item-icon>
                  <component
                    :is="item.icon"
                    :class="{
                      'smaller-icon': item.smallerIcon,
                      'stroke-not-fill': item.useStrokeNotFill,
                    }"
                    class="sidebar-menu-icon"
                  />
                  <span v-if="item.badge" :class="{ 'square-badge': item.squareBadge }" class="badge">
                    {{ item.badge }}
                  </span>
                </v-list-item-icon>
                <v-list-item-content v-if="item.title || item.subtitle" class="pa-0">
                  <v-list-item-title class="ma-0 text-16 d-flex align-center">
                    <span>{{ item.title }}</span>
                    <span v-if="item.badge" :class="{ 'square-badge': item.squareBadge }" class="badge">
                      {{ item.badge }}
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <span class="text-subtitle-2 font-weight-600">
              {{ item.title }}
            </span>
          </v-tooltip>
        </div>
      </div>
    </v-list>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
  .close-menu-btn {
    position: absolute;
    right: -45px;
    top: 30px;
    background: #361550;
    border-radius: 5px;
  }

  .scrollable {
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100vh - 90px);
    padding-right: 10px;
    scrollbar-color: #350f53 #1c0630;
    scrollbar-width: thin;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar-thumb {
      background: #350f53 !important;
    }

    &::-webkit-scrollbar-track {
      background: #1c0630 !important;
    }

    &--suspended {
      margin-top: 12px;
    }

    @media screen and (max-width: 960px) {
      height: calc(100vh - 128px);
      padding-bottom: 140px;
    }

    &::-webkit-scrollbar {
      width: 3px !important;
    }
  }

  .logo-wrapper {
    margin: 30px auto;
    display: flex;
    justify-content: center;
  }

  .badge {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    border-radius: 100%;
    min-width: 22px;
    height: 22px;
    background: #ba1919;
    font-weight: bold;
    text-align: center;
    padding: 0 1px 0 0;
    font-size: 10px;
    font-variant-numeric: tabular-nums;

    &.square-badge {
      height: 18px;
      border-radius: 4px;
      padding: 4px 8px;
      font-weight: 600;
    }
  }

  .v-navigation-drawer {
    &:not(.v-navigation-drawer--mini-variant) {
      .v-list-item__icon .badge {
        display: none;
      }
    }

    &.v-navigation-drawer--mini-variant {
      .v-list-item__content {
        display: none;
      }

      .v-list-item__icon {
        position: relative;

        .badge {
          position: absolute;
          top: 0;
          right: 8px;
          transform: translateX(100%);
          min-width: 16px;
          height: 16px;

          &.square-badge {
            font-size: 8px;
            right: 19px;
          }
        }
      }
    }
  }

  .menu-item {
    display: flex;
    align-items: center;
    height: 40px;
    margin: 6px auto;
    border-radius: 10px;
    will-change: transform, opacity;
    position: relative;
    font-weight: 500;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 6px;
      transform: scale(0);
      transform-origin: left;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
      background: rgba(255, 255, 255, 0.6);
      transition: all 0.2s ease-in-out;
    }

    &.active-route {
      font-weight: bold;
      background: #361550;

      &::after {
        background: var(--v-gold-base);
        transform: scale(1);
      }

      .sidebar-menu-icon * {
        fill: var(--v-gold-base);
      }

      .sidebar-menu-icon.stroke-not-fill path {
        fill: none;
        stroke: var(--v-gold-base);
      }
    }

    &::before {
      border-radius: 8px;
    }

    &.colored {
      color: currentColor;

      .sidebar-menu-icon * {
        fill: currentColor;
      }

      &::v-deep .v-list-item__title {
        color: currentColor;
      }
    }

    &::v-deep .v-list-item__title {
      color: #fff;
    }
  }

  .sidebar-divider {
    background: #361550;
    border: none;
    height: 1px;
    margin: 10px 8px;
  }

  .logout-item {
    margin-bottom: 75px;
  }

  .hidden-tooltip {
    display: none;
  }

  .hidden-tooltip-mobile {
    @media screen and (max-width: 993px) {
      display: none;
    }
  }

  .v-tooltip__content {
    background: black;
    border-radius: 10px;
    left: 55px !important;

    &:before {
      content: '';
      position: absolute;
      height: 10px;
      width: 7px;
      background: black;
      top: 50%;
      transform: translate(0, -50%);
      left: -7px;
      clip-path: polygon(100% 0, 0 50%, 100% 100%);
    }
  }

  .menu-toggle-container {
    height: 82px;
    padding-left: 0;
    padding-bottom: 12px;

    .menu-toggle {
      position: relative;
      background: #361550;
      border-radius: 10px;
      transition: all 0.2s ease-in-out;

      .badge {
        position: absolute;
        top: -2px;
        right: 2px;
      }
    }
  }

  .nav-drawer.v-navigation-drawer {
    background-color: #24073d !important;
    overflow: hidden;
    padding-right: 10px !important;

    &::v-deep {
      .v-navigation-drawer__border {
        background: none !important;
      }

      .v-navigation-drawer__content {
        overflow-y: hidden !important;
      }
    }

    @media screen and (max-width: 960px) {
      z-index: 11;

      &.v-navigation-drawer--open::after {
        content: '';
        position: fixed;
        top: 0;
        left: 100%;
        height: 100vh;
        width: 100vw;
        filter: blur(15px);
        backdrop-filter: blur(15px);
        z-index: -1;
        pointer-events: none;
      }
    }

    @media screen and (max-width: 600px) {
      width: 80% !important;
    }
  }

  .v-list-item {
    padding: 4px;
  }

  .v-list-item__icon {
    margin: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    svg {
      width: 60px;
      height: 40px;
    }
  }

  .closed-drawer {
    transform: rotate(180deg);
  }

  .selected-category::v-deep {
    i {
      transform: rotate(180deg);
    }
  }

  .drawer-close {
    .child-list {
      display: none !important;
    }

    .v-list-item__icon {
      margin-right: 0 !important;
    }
  }

  .sidebar-menu-icon {
    &.smaller-icon {
      height: 20px;
    }
  }
</style>
